import { ReactNode } from 'react';
import { ExportButton } from '../../../../utils/export';
import { ExportFormat } from '../../../../utils/export/types';
import { SkeletonLoader } from '../../shared/SkeletonLoader';
import { useIsMobile } from '../../../../hooks/useIsMobile';

interface AnalyticsCardWrapperProps {
  title: string;
  subtitle?: string;
  headerActions?: ReactNode;
  isLoading: boolean;
  isError: boolean;
  error?: Error | null;
  isEmpty: boolean;
  emptyMessage?: string;
  height?: string;
  children: ReactNode;
  exportData?: any[];
  exportFileName?: string;
  accent?: string;
}

export function AnalyticsCardWrapper({
  title,
  subtitle,
  headerActions,
  isLoading,
  isError,
  error,
  isEmpty,
  emptyMessage = 'No data available',
  height = 'h-96',
  children,
  exportData,
  exportFileName,
  accent,
}: AnalyticsCardWrapperProps) {
  const isMobile = useIsMobile();
  const LoadingState = () => <SkeletonLoader variant="chart" height={height} />;

  const ErrorState = () => (
    <div className={`flex items-center justify-center ${height} text-[var(--anl-text-muted)]`}>
      <div className="rounded-lg border border-[var(--anl-border)] bg-[var(--anl-surface-elev)] p-6 text-center">
        <p className="mb-2 text-sm text-[var(--anl-error)]">Unable to load data</p>
        {error && (
          <p className="mb-3 text-xs text-[var(--anl-text-muted)]">
            {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        )}
      </div>
    </div>
  );

  const EmptyState = () => (
    <div className={`flex items-center justify-center ${height} text-[var(--anl-text-muted)]`}>
      <div className="text-center">
        <p className="text-sm">{emptyMessage}</p>
        <p className="mt-1 text-xs text-[var(--anl-text-muted)]">Try adjusting your filters</p>
      </div>
    </div>
  );

  return (
    <div className="relative rounded-lg border border-[var(--anl-border)] bg-[var(--anl-surface)] p-4">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className={`font-semibold text-[var(--anl-text)] ${isMobile ? 'text-base' : 'text-lg'}`}>{title}</h3>
          {subtitle && <div className="mt-1 text-sm text-[var(--anl-text-muted)]">{subtitle}</div>}
        </div>
        <div className="flex items-center space-x-2">
          {exportData && exportData.length > 0 && (
            <ExportButton
              data={exportData}
              config={{
                fileName: exportFileName || title.toLowerCase().replace(/\s+/g, '-'),
                date: true,
              }}
              format={ExportFormat.CSV}
              className="text-[var(--anl-text-muted)] hover:text-[var(--anl-text)]"
            />
          )}
          {headerActions}
        </div>
      </div>

      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState />
      ) : isEmpty ? (
        <EmptyState />
      ) : (
        <div className={height}>{children}</div>
      )}
      {accent ? (
        <div
          className="absolute bottom-0 left-0 right-0 rounded-b-lg"
          style={{ height: 6, backgroundColor: accent }}
        />
      ) : null}
    </div>
  );
}
